import {inject, Injectable} from '@angular/core';
import {SelectItem} from './vehicle-detail/SelectItem';
import {BehaviorSubject, Observable} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {filter, first, map} from 'rxjs/operators';
import {TdsTranslationData} from './TdsTranslationData';
import {TranslateService} from '@ngx-translate/core';
import {APP_CONFIG} from "./app.config";

/**
 * Containts both ucmim specific and type data translations.
 */
@Injectable({
  providedIn: 'root',
})
export class UcmimTranslationService {
  private translateService = inject(TranslateService);

  private readonly tdsData = new BehaviorSubject<TdsTranslationData | undefined>(undefined);
  private readonly appConfig = inject(APP_CONFIG);

  constructor() {
    const httpClient = inject(HttpClient);

    httpClient
      .get<TdsTranslationData>(
        `/api/translations?country=${this.appConfig.tdsMarket}`,
      )
      .subscribe((responseData) => this.tdsData.next(responseData));
  }

  /**
   * Colors used.
   *
   * Detailed color text.
   */
  getColorsUsed(): Observable<SelectItem[]> {
    return this.getTdsDataWhenReady().pipe(map((td) => td.colors));
  }

  /**
   * Get used equipment.
   *
   * Sorted by text.
   */
  getUsedEquipment(): Observable<SelectItem[]> {
    return this.getTdsDataWhenReady().pipe(
      filter((td) => !!td),
      map((td) =>
        td.equipments.sort((a, b) =>
          a.description.localeCompare(b.description),
        ),
      ),
    );
  }

  getTdsDataWhenReady(): Observable<TdsTranslationData> {
    return this.tdsData.asObservable().pipe(filter((data) => !!data));
  }

  /**
   * Get select items retrieved by the given object key.
   *
   * @param translationKey key of the object to transform
   */
  getSelectItems(translationKey: string): Observable<SelectItem[]> {
    return this.translateService.get(translationKey).pipe(
      first(),
      map((value) => this.toSelectItems(value)),
    );
  }

  /**
   * Translates the given key.
   *
   * @param key - key of the string which will be translated
   */
  getSelectedKey(key: string) {
    return this.translateService.instant(key);
  }

  private toSelectItems(value: object): SelectItem[] {
    if (!value) {
      return [];
    }
    return Object.keys(value).map((key) => ({
      description: (value as any)[key],
      code: key,
    }));
  }
}
